// src/components/landing/animations/UploadStampAnimation.js
import React, { useState, useEffect } from 'react';
import { motion, useTransform } from 'framer-motion';

/**
 * UPLOAD & STAMP ANIMATION (Combined, Time-Based)
 * 
 * Plays automatically on a timer when in view.
 * Scroll only controls fade in/out at boundaries.
 * 
 * Animation sequence:
 * 1. Docs drop in (0-600ms)
 * 2. Hand slides in, docs enlarge (600-1100ms)
 * 3. Hand closes, dropzone appears (1100-1300ms)
 * 4. Drag to dropzone with dangle (1300-1800ms)
 * 5. Hand opens (1800-1900ms)
 * 6. Docs shrink and settle (1900-2000ms)
 * 7. Dropzone stretches, docs spread (2000-2400ms)
 * 8. Stamps drop in one by one (2400-3400ms)
 * 9. Hold final state, loop after delay
 * 
 * Frame: 1024x576px
 */

const FRAME = { width: 1024, height: 576 };
const TOTAL_DURATION = 3500; // Total animation duration in ms
const LOOP_DELAY = 1000; // Delay before looping
const START_TIME = 0;
const DROP_IN_TIME = 600;
const HAND_HOVER_TIME = 1100;
const HAND_CLOSE_TIME = 1300;
const DRAG_TIME = 1800;
const HAND_DROP_TIME = 1900;
const DOCS_SETTLE_TIME = 2100;
const DROPZONE_GROW_TIME = 2500;
const STAMP_START_TIME = 2600;
const STAMP_END_TIME = 3500;

const CONFIG = {
  doc: {
    width: 256,
    height: 320,
  },
  
  hand: {
    width: 64,
  },
  
  stamp: {
    width: 64,
    height: 36,
  },
};

const UploadStampAnimation = ({ scrollProgress }) => {
  const [animationTime, setAnimationTime] = useState(0);

  // Start animation when component mounts
  useEffect(() => {
    let startTime = Date.now();
    let animationFrameId;
    
    const updateAnimation = () => {
      const elapsed = Date.now() - startTime;
      const loopTime = elapsed % (TOTAL_DURATION + LOOP_DELAY);
      
      // Hold at end for LOOP_DELAY, then restart
      if (loopTime > TOTAL_DURATION) {
        setAnimationTime(TOTAL_DURATION);
      } else {
        setAnimationTime(loopTime);
      }
      
      animationFrameId = requestAnimationFrame(updateAnimation);
    };
    
    animationFrameId = requestAnimationFrame(updateAnimation);
    
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  // Scroll-based fade in/out (independent of animation)
  const fadeOpacity = useTransform(
    scrollProgress,
    [0, 0.1, 0.9, 1],
    [0, 1, 1, 0]
  );
  
  const fadeY = useTransform(
    scrollProgress,
    [0.9, 1],
    [0, -50]
  );

  // =================================================================
  // ANIMATION KEYFRAMES (time-based)
  // =================================================================

  // Helper to interpolate based on time
  const lerp = (start, end, t) => start + (end - start) * t;
  
  // Get value at specific time range
  const getValueAtTime = (timeStart, timeEnd, valueStart, valueEnd) => {
    if (animationTime < timeStart) return valueStart;
    if (animationTime >= timeEnd) return valueEnd;
    const t = (animationTime - timeStart) / (timeEnd - timeStart);
    return lerp(valueStart, valueEnd, t);
  };

  // =================================================================
  // STEP 1-2: DOCS DROP IN (0-600ms)
  // =================================================================
  
  const doc1Y = getValueAtTime(START_TIME, DROP_IN_TIME/3, 0, 128);
  const doc1Opacity = getValueAtTime(START_TIME, DROP_IN_TIME/3, 0, 1);
  
  const doc2Y = getValueAtTime(DROP_IN_TIME/3, 2 * DROP_IN_TIME/3, 0, 128);
  const doc2Opacity = getValueAtTime(DROP_IN_TIME/3, 2 * DROP_IN_TIME/3, 0, 1);
  
  const doc3Y = getValueAtTime(2 * DROP_IN_TIME/3, DROP_IN_TIME, 0, 128);
  const doc3Opacity = getValueAtTime(2 * DROP_IN_TIME/3, DROP_IN_TIME, 0, 1);

  // =================================================================
  // STEP 3: HAND SLIDES IN, DOCS ENLARGE (600-1100ms)
  // =================================================================
  
  const handX = (() => {
    if (animationTime < DROP_IN_TIME) return 0;
    if (animationTime < HAND_HOVER_TIME) return getValueAtTime(DROP_IN_TIME, HAND_HOVER_TIME, 0, 148);
    if (animationTime < HAND_CLOSE_TIME) return 148;
    if (animationTime < DRAG_TIME) return getValueAtTime(HAND_CLOSE_TIME, DRAG_TIME, 148, 794);
    if (animationTime < DOCS_SETTLE_TIME) return 794;
    // Fade out during spread
    return 794;
  })();
  
  const handY = (() => {
    if (animationTime < HAND_HOVER_TIME) return getValueAtTime(DROP_IN_TIME, HAND_HOVER_TIME, 0, 256);
    if (animationTime < HAND_CLOSE_TIME) return getValueAtTime(HAND_HOVER_TIME, HAND_CLOSE_TIME, 256, 265); // Closed hand lower
    if (animationTime < DRAG_TIME) return 265;
    if (animationTime < HAND_DROP_TIME) return getValueAtTime(DRAG_TIME, HAND_DROP_TIME, 265, 256); // Open hand higher
    return 256;
  })();
  
  const handOpacity = (() => {
    if (animationTime < DROP_IN_TIME) return 0;
    if (animationTime < (DROP_IN_TIME + HAND_HOVER_TIME)/2) return getValueAtTime(DROP_IN_TIME, (DROP_IN_TIME + HAND_HOVER_TIME)/2, 0, 1); // Fade in during first quarter
    if (animationTime < DOCS_SETTLE_TIME) return 1;
    return getValueAtTime(DOCS_SETTLE_TIME, DOCS_SETTLE_TIME + 100, 1, 0); // Fade out during spread
  })();
  
  // Hand state (open vs closed)
  const isHandClosed = animationTime >= HAND_HOVER_TIME && animationTime < HAND_DROP_TIME;
  
  // Docs enlarge when hand reaches them
  const docScale = (() => {
    if (animationTime < (DROP_IN_TIME + HAND_HOVER_TIME)/2) return 1; // Hand at 1/4 point
    if (animationTime < HAND_HOVER_TIME) return getValueAtTime((DROP_IN_TIME + HAND_HOVER_TIME)/2, HAND_HOVER_TIME, 1, 1.125);
    if (animationTime < HAND_DROP_TIME) return 1.125;
    return getValueAtTime(HAND_DROP_TIME, DOCS_SETTLE_TIME, 1.125, 1); // Shrink back
  })();

  // =================================================================
  // DOCUMENT POSITIONS
  // =================================================================
  
  const doc1X = (() => {
    if (animationTime < (DROP_IN_TIME + HAND_HOVER_TIME)/2) return 18;
    if (animationTime < HAND_HOVER_TIME) return getValueAtTime((DROP_IN_TIME + HAND_HOVER_TIME)/2, HAND_HOVER_TIME, 18, 0); // Shift during enlarge
    if (animationTime < HAND_CLOSE_TIME) return 0;
    if (animationTime < DRAG_TIME) return getValueAtTime(HAND_CLOSE_TIME, DRAG_TIME, 0, 646); // Drag to dropzone
    if (animationTime < HAND_DROP_TIME) return 646;
    if (animationTime < DOCS_SETTLE_TIME) return getValueAtTime(HAND_DROP_TIME, DOCS_SETTLE_TIME, 646, 664); // Settle
    if (animationTime < DROPZONE_GROW_TIME) return getValueAtTime(DOCS_SETTLE_TIME, DROPZONE_GROW_TIME, 664, 72); // Spread
    return 72;
  })();
  
  const doc2X = (() => {
    if (animationTime < (DROP_IN_TIME + HAND_HOVER_TIME)/2) return 34;
    if (animationTime < HAND_HOVER_TIME) return getValueAtTime((DROP_IN_TIME + HAND_HOVER_TIME)/2, HAND_HOVER_TIME, 34, 18);
    if (animationTime < HAND_CLOSE_TIME) return 18;
    if (animationTime < DRAG_TIME) return getValueAtTime(HAND_CLOSE_TIME, DRAG_TIME, 18, 664);
    if (animationTime < HAND_DROP_TIME) return 664;
    if (animationTime < DOCS_SETTLE_TIME) return getValueAtTime(HAND_DROP_TIME, DOCS_SETTLE_TIME, 664, 680);
    if (animationTime < DROPZONE_GROW_TIME) return getValueAtTime(DOCS_SETTLE_TIME, DROPZONE_GROW_TIME, 680, 384);
    return 384;
  })();
  
  const doc3X = (() => {
    if (animationTime < (DROP_IN_TIME + HAND_HOVER_TIME)/2) return 50;
    if (animationTime < HAND_HOVER_TIME) return getValueAtTime((DROP_IN_TIME + HAND_HOVER_TIME)/2, HAND_HOVER_TIME, 50, 36);
    if (animationTime < HAND_CLOSE_TIME) return 36;
    if (animationTime < DRAG_TIME) return getValueAtTime(HAND_CLOSE_TIME, DRAG_TIME, 36, 682);
    if (animationTime < HAND_DROP_TIME) return 682;
    if (animationTime < DOCS_SETTLE_TIME) return getValueAtTime(HAND_DROP_TIME, DOCS_SETTLE_TIME, 682, 696);
    return 696; // Final spread position
  })();
  
  const doc1YFinal = (() => {
    if (animationTime < DROP_IN_TIME/3) return doc1Y;
    if (animationTime < (DROP_IN_TIME + HAND_HOVER_TIME)/2) return 128;
    if (animationTime < HAND_HOVER_TIME) return getValueAtTime((DROP_IN_TIME + HAND_HOVER_TIME)/2, HAND_HOVER_TIME, 128, 108); // Shift during enlarge
    if (animationTime < HAND_DROP_TIME) return 108;
    return getValueAtTime(HAND_DROP_TIME, DOCS_SETTLE_TIME, 108, 128); // Back to 128
  })();
  
  const doc2YFinal = (() => {
    if (animationTime < 2 * DROP_IN_TIME/3) return doc2Y;
    if (animationTime < (DROP_IN_TIME + HAND_HOVER_TIME)/2) return 128;
    if (animationTime < HAND_HOVER_TIME) return getValueAtTime((DROP_IN_TIME + HAND_HOVER_TIME)/2, HAND_HOVER_TIME, 128, 108);
    if (animationTime < HAND_DROP_TIME) return 108;
    return getValueAtTime(HAND_DROP_TIME, DOCS_SETTLE_TIME, 108, 128);
  })();
  
  const doc3YFinal = (() => {
    if (animationTime < DROP_IN_TIME) return doc3Y;
    if (animationTime < (DROP_IN_TIME + HAND_HOVER_TIME)/2) return 128;
    if (animationTime < HAND_HOVER_TIME) return getValueAtTime((DROP_IN_TIME + HAND_HOVER_TIME)/2, HAND_HOVER_TIME, 128, 108);
    if (animationTime < HAND_DROP_TIME) return 108;
    return getValueAtTime(HAND_DROP_TIME, DOCS_SETTLE_TIME, 108, 128);
  })();

  // =================================================================
  // DANGLE ROTATION (during drag, 1300-1800ms)
  // =================================================================
  
  const dangleRotation1 = (() => {
    if (animationTime < HAND_CLOSE_TIME) return 0;
    if (animationTime < DRAG_TIME) return getValueAtTime(HAND_CLOSE_TIME, DRAG_TIME, 0, 5);
    if (animationTime < DOCS_SETTLE_TIME) return getValueAtTime(DRAG_TIME, DOCS_SETTLE_TIME, 5, 0); // Straighten during spread
    return 0;
  })();
  
  const dangleRotation2 = (() => {
    if (animationTime < HAND_CLOSE_TIME) return 0;
    if (animationTime < DRAG_TIME) return getValueAtTime(HAND_CLOSE_TIME, DRAG_TIME, 0, 2);
    if (animationTime < DOCS_SETTLE_TIME) return getValueAtTime(DRAG_TIME, DOCS_SETTLE_TIME, 2, 0);
    return 0;
  })();
  
  const dangleRotation3 = (() => {
    if (animationTime < HAND_CLOSE_TIME) return 0;
    if (animationTime < DRAG_TIME) return getValueAtTime(HAND_CLOSE_TIME, DRAG_TIME, 0, -5);
    if (animationTime < DOCS_SETTLE_TIME) return getValueAtTime(DRAG_TIME, DOCS_SETTLE_TIME, -5, 0);
    return 0;
  })();

  // =================================================================
  // DROPZONE (appears at 1100ms, stretches at 2000ms)
  // =================================================================
  
  const dropzoneOpacity = getValueAtTime(HAND_HOVER_TIME, HAND_CLOSE_TIME, 0, 1);
  
  const dropzoneX = (() => {
    if (animationTime < DOCS_SETTLE_TIME) return 592;
    return getValueAtTime(DOCS_SETTLE_TIME, DROPZONE_GROW_TIME, 592, 0);
  })();
  
  const dropzoneWidth = (() => {
    if (animationTime < DOCS_SETTLE_TIME) return 432;
    return getValueAtTime(DOCS_SETTLE_TIME, DROPZONE_GROW_TIME, 432, 1024);
  })();

  // =================================================================
  // STAMPS (drop in 2400-3400ms)
  // =================================================================
  
  const stamp1Y = getValueAtTime(STAMP_START_TIME, (STAMP_END_TIME + 2 * STAMP_START_TIME)/3, 348, 396); // 100ms delay then 300ms drop
  const stamp1Opacity = getValueAtTime(STAMP_START_TIME, (STAMP_END_TIME + 2 * STAMP_START_TIME)/3, 0, 1);
  
  const stamp2Y = getValueAtTime((STAMP_END_TIME + 2 * STAMP_START_TIME)/3, (2 * STAMP_END_TIME + STAMP_START_TIME)/3, 348, 396);
  const stamp2Opacity = getValueAtTime((STAMP_END_TIME + 2 * STAMP_START_TIME)/3, (2 * STAMP_END_TIME + STAMP_START_TIME)/3, 0, 1);
  
  const stamp3Y = getValueAtTime((2 * STAMP_END_TIME + STAMP_START_TIME)/3, STAMP_END_TIME, 348, 396);
  const stamp3Opacity = getValueAtTime((2 * STAMP_END_TIME + STAMP_START_TIME)/3, STAMP_END_TIME, 0, 1);

  return (
    <motion.div 
      className="w-full h-full flex items-center justify-center overflow-visible"
      style={{ 
        opacity: fadeOpacity,
        y: fadeY
      }}
    >
      <svg
        viewBox={`0 0 ${FRAME.width} ${FRAME.height}`}
        className="w-full h-auto"
      >
        {/* DROPZONE */}
        <rect
          x={dropzoneX}
          y={32}
          width={dropzoneWidth}
          height={512}
          rx={32}
          fill="none"
          stroke="#8F99A3"
          strokeWidth={4}
          strokeDasharray="10 8"
          opacity={dropzoneOpacity}
        />
        
        {/* DOCUMENT 1 */}
        <g
          transform={`translate(${doc1X}, ${doc1YFinal}) scale(${docScale}) rotate(${dangleRotation1})`}
          opacity={doc1Opacity}
        >
          <image
            href="/doc.svg"
            width={CONFIG.doc.width}
            height={CONFIG.doc.height}
            x={0}
            y={0}
          />
        </g>
        
        {/* DOCUMENT 2 */}
        <g
          transform={`translate(${doc2X}, ${doc2YFinal}) scale(${docScale}) rotate(${dangleRotation2})`}
          opacity={doc2Opacity}
        >
          <image
            href="/doc.svg"
            width={CONFIG.doc.width}
            height={CONFIG.doc.height}
            x={0}
            y={0}
          />
        </g>
        
        {/* DOCUMENT 3 */}
        <g
          transform={`translate(${doc3X}, ${doc3YFinal}) scale(${docScale}) rotate(${dangleRotation3})`}
          opacity={doc3Opacity}
        >
          <image
            href="/doc.svg"
            width={CONFIG.doc.width}
            height={CONFIG.doc.height}
            x={0}
            y={0}
          />
        </g>
        
        {/* STAMPS */}
        <g transform={`translate(248, ${stamp1Y})`} opacity={stamp1Opacity}>
          <image
            href="/stamp_animated.svg"
            width={CONFIG.stamp.width}
            height={CONFIG.stamp.height}
          />
        </g>
        
        <g transform={`translate(560, ${stamp2Y})`} opacity={stamp2Opacity}>
          <image
            href="/stamp_animated.svg"
            width={CONFIG.stamp.width}
            height={CONFIG.stamp.height}
          />
        </g>
        
        <g transform={`translate(872, ${stamp3Y})`} opacity={stamp3Opacity}>
          <image
            href="/stamp_animated.svg"
            width={CONFIG.stamp.width}
            height={CONFIG.stamp.height}
          />
        </g>
        
        {/* HAND - OPEN */}
        <g
          transform={`translate(${handX}, ${handY})`}
          opacity={handOpacity * (isHandClosed ? 0 : 1)}
        >
          <image
            href="/hand_open.svg"
            width={CONFIG.hand.width}
            height={CONFIG.hand.width}
            x={0}
            y={0}
          />
        </g>
        
        {/* HAND - CLOSED */}
        <g
          transform={`translate(${handX}, ${handY})`}
          opacity={handOpacity * (isHandClosed ? 1 : 0)}
        >
          <image
            href="/hand_closed.svg"
            width={CONFIG.hand.width}
            height={CONFIG.hand.width}
            x={0}
            y={0}
          />
        </g>
      </svg>
    </motion.div>
  );
};

export default UploadStampAnimation;